/* eslint-disable no-unused-vars */
/* eslint-disable */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Table, Spin, Button, message } from "antd";
import { LinkOutlined, DownloadOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { HubCPOverviewService } from "../../../services/api/hubCPOverviewService";
import "./index.scss";

const ShipmentDetailsModal = ({
  visible,
  onCancel,
  title,
  data,
  loading,
  pagination,
  onTableChange,
  totalCount,
  selectedPeriod,
  summaryData,
  viewAllLink,
  operationCodes,
  hubId,
  dateFilter,
  isPieChart = false, // New prop to identify if it's the pie chart report
  status, // Passed from StatusDistribution
  dateRange, // Date range from parent
}) => {
  const navigate = useNavigate();

  // Define columns based on report type
  let columns = [];

  if (isPieChart) {
    // Columns for Pie Chart Clickable Report
    columns = [
      {
        title: "Booking Date",
        dataIndex: "bookingDate",
        key: "bookingDate",
        width: 150,
      },
      {
        title: "Tracking ID",
        dataIndex: "trackingId",
        key: "trackingId",
        width: 180,
        render: (text) => (
          <span
            style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              if (text) {
                const url = `/shipment-tracker?awb=${text}`;
                window.open(url, "_blank");
              }
            }}
          >
            {text || "-"}
          </span>
        ),
      },
      {
        title: "Movement Type",
        dataIndex: "movementType",
        key: "movementType",
        width: 150,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 150,
      },
      {
        title: "Document Type",
        dataIndex: "documentType",
        key: "documentType",
        width: 150,
      },
      {
        title: "Service Type",
        dataIndex: "serviceType",
        key: "serviceType",
        width: 150,
      },
      {
        title: "Origin Hub",
        dataIndex: "originHub",
        key: "originHub",
        width: 200,
      },
      {
        title: "Current Location",
        dataIndex: "currentLocation",
        key: "currentLocation",
        width: 200,
      },
      {
        title: "Destination Hub",
        dataIndex: "destinationHub",
        key: "destinationHub",
        width: 200,
      },
      {
        title: "Latest Status Time",
        dataIndex: "latestStatusTime",
        key: "latestStatusTime",
        width: 180,
      },
      {
        title: "Ageing (Days)",
        dataIndex: "ageingFromStatusDateInDays",
        key: "ageingFromStatusDateInDays",
        width: 120,
      },
    ];
  } else {
    // Default columns (Existing)
    columns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 120,
      },
      {
        title: "AWB Number",
        dataIndex: "awbNumber",
        key: "awbNumber",
        width: 180,
        render: (text) => (
          <span
            style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              if (text) {
                const url = `/shipment-tracker?awb=${text}`;
                window.open(url, "_blank");
              }
            }}
          >
            {text || "-"}
          </span>
        ),
      },
      {
        title: "Operation Hub",
        dataIndex: "operationHub",
        key: "operationHub",
        width: 200,
      },
      {
        title: "Origin Hub",
        dataIndex: "originHub",
        key: "originHub",
        width: 200,
        render: (text) => text || "-",
      },
      {
        title: "Destination Hub",
        dataIndex: "destinationHub",
        key: "destinationHub",
        width: 200,
        render: (text) => text || "-",
      },
    ];
  }

  const getPeriodLabel = (period) => {
    switch (period) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "tillDate":
        return "Till Date";
      default:
        return period;
    }
  };

  return (
    <Modal
      title={title || "Shipment Details"}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={1000}
      className="shipment-details-modal"
    >
      {summaryData && (
        <div className="modal-summary">
          <div className="summary-item">
            {selectedPeriod && (
              <div className="summary-row">
                <span className="summary-label">Period:</span>
                <span className="summary-value">{getPeriodLabel(selectedPeriod)}</span>
              </div>
            )}
            {summaryData.count !== undefined && (
              <div className="summary-row">
                <span className="summary-label">Count:</span>
                <span className="summary-value">{summaryData.count.toLocaleString()}</span>
              </div>
            )}
            {summaryData.percentage !== undefined && (
              <div className="summary-row">
                <span className="summary-label">Percentage:</span>
                <span className="summary-value">{summaryData.percentage}%</span>
              </div>
            )}
            {totalCount !== undefined && (
              <div className="summary-row">
                <span className="summary-label">Total Count:</span>
                <span className="summary-value">{totalCount.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          onChange={onTableChange}
          rowKey={(record, index) => `${record.awbNumber}-${index}`}
          scroll={{ x: 900 }}
          size="small"
          locale={{ emptyText: "No shipment details available" }}
        />
      </Spin>

      <div className="modal-footer-link">
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          style={{ marginRight: 8 }}
          onClick={async () => {
            try {
              let url;
              if (isPieChart) {
                const fromDate = dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null;
                const toDate = dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null;
                // Use new download API for pie chart
                url = await HubCPOverviewService.getPieChartHubManagerOverviewClickableDownload({
                  currentHubId: hubId,
                  status: status,
                  fromDate,
                  toDate,
                });
              } else {
                // Use existing download API
                url = await HubCPOverviewService.getClickableHubManagerDashboardDownload({
                  dateFilter: dateFilter,
                  operationCodes: operationCodes,
                  hubId: hubId,
                  status: summaryData?.name || null,
                });
              }
              if (url) {
                window.open(url, "_blank");
              }
            } catch (error) {
              message.error(error?.message || "Failed to download report");
              console.error("Download error:", error);
            }
          }}
        >
          Download
        </Button>
        <Button
          type="link"
          icon={<LinkOutlined />}
          onClick={() => {
            if ((operationCodes && hubId && dateFilter) || (isPieChart && hubId)) {
              navigate("/hub-manager-shipment-details", {
                state: {
                  operationCodes,
                  hubId,
                  dateFilter,
                  title: title || "Shipment Details",
                  selectedPeriod,
                  isPieChart,
                  status,
                  fromDate: dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null,
                  toDate: dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null,
                },
              });
            } else if (viewAllLink?.onClick) {
              viewAllLink.onClick();
            }
          }}
          href={viewAllLink?.href}
          target={viewAllLink?.target || "_blank"}
        >
          {viewAllLink?.text || "View All Shipments"}
        </Button>
      </div>
    </Modal>
  );
};

ShipmentDetailsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  pagination: PropTypes.object.isRequired,
  onTableChange: PropTypes.func.isRequired,
  totalCount: PropTypes.number,
  selectedPeriod: PropTypes.oneOf(["today", "yesterday", "tillDate"]),
  summaryData: PropTypes.shape({
    count: PropTypes.number,
    percentage: PropTypes.string,
  }),
  viewAllLink: PropTypes.shape({
    text: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    target: PropTypes.string,
  }),
  operationCodes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hubId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dateFilter: PropTypes.string,
  isPieChart: PropTypes.bool,
  status: PropTypes.string,
  dateRange: PropTypes.array,
};

export default ShipmentDetailsModal;

